/**
 * RUM Actions Tracking Module
 *
 * Exports all 43 RUM tracking functions for Procright frontend.
 * Each function corresponds to a specific user action defined in rum-actions-map.md.
 *
 * Usage:
 * ```typescript
 * import { trackSpecificationCreationStart, trackSpecificationSaved } from '@/lib/rum-actions';
 *
 * // On specification creation
 * trackSpecificationCreationStart(userId, companyId, specificationId);
 *
 * // On specification save
 * trackSpecificationSaved(specificationId, itemCount, 'manual', saveDurationMs);
 * ```
 */
import { datadogRum } from '@datadog/browser-rum';
import {
  startJourneyTiming,
  completeJourneyTiming,
  trackAIOperationTiming,
  APDEX_THRESHOLDS,
  type JourneyName,
  type AIOperationType,
} from './rum-sli-tracking';

// ============================================================================
// Journey 1: Specification Creation (12 actions)
// ============================================================================

/**
 * RUM-001: Track specification creation start
 * Trigger: User clicks "Create New Specification" button
 */
export const trackSpecificationCreationStart = (
  userId: string,
  companyId: string,
  specificationId: string
): void => {
  // Start Apdex timing for this specification journey
  startJourneyTiming(specificationId, 'specification_creation');

  datadogRum.addAction('specification_creation_start', {
    user: {
      id: userId,
      company_id: companyId,
    },
    specification: {
      id: specificationId,
    },
    journey: {
      name: 'specification_creation',
      flow_stage: 'initiation',
      apdex_threshold_satisfied: APDEX_THRESHOLDS.specification_creation.satisfied,
      apdex_threshold_tolerating: APDEX_THRESHOLDS.specification_creation.tolerating,
    },
    sli: {
      timing_started: true,
      timestamp: Date.now(),
    },
  });
};

/**
 * RUM-002: Track AI generation initiated
 * Trigger: User requests AI-assisted specification generation
 */
export const trackSpecificationAIGenerationInitiated = (
  specificationId: string,
  categoryId: string,
  promptLength: number
): void => {
  datadogRum.addAction('specification_ai_generation_initiated', {
    specification: {
      id: specificationId,
      category_id: categoryId,
    },
    ai: {
      prompt_length: promptLength,
      generation_type: 'ai_assisted',
      model_version: 'gpt-4',
    },
    performance: {
      initiation_time: Date.now(),
    },
  });
};

/**
 * RUM-003: Track AI generation complete
 * Trigger: AI generation successfully returns results
 */
export const trackSpecificationAIGenerationComplete = (
  specificationId: string,
  itemsGenerated: number,
  generationTimeMs: number,
  success: boolean
): void => {
  // Track AI operation timing for P95/P99 SLOs
  trackAIOperationTiming(specificationId, 'generation', generationTimeMs, success);

  datadogRum.addAction('specification_ai_generation_complete', {
    specification: {
      id: specificationId,
      items_generated: itemsGenerated,
    },
    ai: {
      generation_time_ms: generationTimeMs,
      success: success,
      items_per_second: itemsGenerated / (generationTimeMs / 1000),
    },
    performance: {
      completion_time: Date.now(),
    },
  });
};

/**
 * RUM-004: Track category selected
 * Trigger: User selects specification category
 */
export const trackSpecificationCategorySelected = (
  specificationId: string,
  categoryId: string,
  categoryName: string,
  selectionSource: 'manual' | 'ai_suggested' | 'template'
): void => {
  datadogRum.addAction('specification_category_selected', {
    specification: {
      id: specificationId,
      category_id: categoryId,
      category_name: categoryName,
    },
    selection: {
      source: selectionSource,
      timestamp: Date.now(),
    },
  });
};

/**
 * RUM-005: Track item added
 * Trigger: User adds new requirement item to specification
 */
export const trackSpecificationItemAdded = (
  specificationId: string,
  itemId: string,
  itemType: string,
  additionMethod: 'manual' | 'ai_generated' | 'template',
  totalItems: number
): void => {
  datadogRum.addAction('specification_item_added', {
    specification: {
      id: specificationId,
      total_items: totalItems,
    },
    item: {
      id: itemId,
      type: itemType,
      addition_method: additionMethod,
    },
  });
};

/**
 * RUM-006: Track item edited
 * Trigger: User edits existing requirement item
 */
export const trackSpecificationItemEdited = (
  specificationId: string,
  itemId: string,
  changeType: 'content' | 'priority' | 'validation',
  editCount: number
): void => {
  datadogRum.addAction('specification_item_edited', {
    specification: {
      id: specificationId,
    },
    item: {
      id: itemId,
      change_type: changeType,
      edit_count: editCount,
    },
  });
};

/**
 * RUM-007: Track item deleted
 * Trigger: User removes requirement item
 */
export const trackSpecificationItemDeleted = (
  specificationId: string,
): void => {
  datadogRum.addAction('specification_item_deleted', {
    specification: {
      id: specificationId,
    },
    item: {
      id: specificationId,
    },
  });
};

/**
 * RUM-008: Track specification saved
 * Trigger: User saves specification (manual or auto-save)
 */
export const trackSpecificationSaved = (
  specificationId: string,
  itemCount: number,
  saveType: 'manual' | 'auto',
  saveDurationMs: number,
  success: boolean = true
): void => {
  // Complete Apdex timing for specification creation journey
  const journeyMetrics = completeJourneyTiming(specificationId, success);

  datadogRum.addAction('specification_saved', {
    specification: {
      id: specificationId,
      item_count: itemCount,
      save_type: saveType,
    },
    performance: {
      save_duration_ms: saveDurationMs,
      timestamp: Date.now(),
    },
    journey: {
      name: 'specification_creation',
      flow_stage: 'completion',
      duration_ms: journeyMetrics?.duration_ms,
      apdex_category: journeyMetrics?.apdex_category,
      apdex_threshold_satisfied: journeyMetrics?.apdex_threshold_satisfied,
      apdex_threshold_tolerating: journeyMetrics?.apdex_threshold_tolerating,
    },
    sli: {
      success: success,
      save_within_p95: saveDurationMs <= 2000,
      save_within_p99: saveDurationMs <= 5000,
      slo_threshold_p95: 2000,
      slo_threshold_p99: 5000,
    },
  });
};

/**
 * RUM-009: Track AI chat message sent
 * Trigger: User sends message in AI chat interface
 */
export const trackSpecificationAIChatMessageSent = (
  specificationId: string,
  messageLength: number,
  messageIntent: string,
  sessionId: string,
  messageCount: number
): void => {
  datadogRum.addAction('specification_ai_chat_message_sent', {
    specification: {
      id: specificationId,
    },
    chat: {
      message_length: messageLength,
      message_intent: messageIntent,
      session_id: sessionId,
      message_count: messageCount,
    },
  });
};

/**
 * RUM-010: Track template applied
 * Trigger: User applies predefined template
 */
export const trackSpecificationTemplateApplied = (
  specificationId: string,
  templateId: string,
  templateName: string,
  itemsAdded: number
): void => {
  datadogRum.addAction('specification_template_applied', {
    specification: {
      id: specificationId,
    },
    template: {
      id: templateId,
      name: templateName,
      items_added: itemsAdded,
    },
  });
};

/**
 * RUM-011: Track specification exported
 * Trigger: User exports specification (PDF, Word, etc.)
 */
export const trackSpecificationExported = (
  specificationId: string,
  exportFormat: 'pdf' | 'docx' | 'xlsx',
  itemCount: number,
  exportDurationMs: number,
  fileSizeKb?: number
): void => {
  datadogRum.addAction('specification_exported', {
    specification: {
      id: specificationId,
      item_count: itemCount,
    },
    export: {
      format: exportFormat,
      duration_ms: exportDurationMs,
      file_size_kb: fileSizeKb,
    },
  });
};

/**
 * RUM-012: Track specification deleted
 * Trigger: User deletes entire specification
 */
export const trackSpecificationDeleted = (
  specificationId: string,
  specAgeDays: number,
  itemCount: number,
  wasCompleted: boolean
): void => {
  datadogRum.addAction('specification_deleted', {
    specification: {
      id: specificationId,
      age_days: specAgeDays,
      item_count: itemCount,
      was_completed: wasCompleted,
    },
  });
};

// ============================================================================
// Journey 2: Product Discovery (10 actions)
// ============================================================================

/**
 * RUM-013: Track product discovery initiated
 * Trigger: User starts product discovery process
 */
export const trackProductDiscoveryInitiated = (
  specificationId: string,
  itemCount: number,
  categoryId: string
): void => {
  // Start Apdex timing for product discovery journey
  startJourneyTiming(`discovery_${specificationId}`, 'product_discovery');

  datadogRum.addAction('product_discovery_initiated', {
    specification: {
      id: specificationId,
      item_count: itemCount,
      category_id: categoryId,
    },
    journey: {
      name: 'product_discovery',
      flow_stage: 'initiation',
      apdex_threshold_satisfied: APDEX_THRESHOLDS.product_discovery.satisfied,
      apdex_threshold_tolerating: APDEX_THRESHOLDS.product_discovery.tolerating,
    },
    sli: {
      timing_started: true,
      timestamp: Date.now(),
    },
  });
};

/**
 * RUM-014: Track product search query submitted
 * Trigger: User submits product search query
 */
export const trackProductSearchQuerySubmitted = (
  specificationId: string,
  queryLength: number,
  filterCount: number,
  searchMethod: 'keyword' | 'ai' | 'category'
): void => {
  datadogRum.addAction('product_search_query_submitted', {
    specification: {
      id: specificationId,
    },
    search: {
      query_length: queryLength,
      has_filters: filterCount > 0,
      filter_count: filterCount,
      search_method: searchMethod,
    },
    performance: {
      query_time: Date.now(),
    },
  });
};

/**
 * RUM-015: Track product ranking complete
 * Trigger: AI ranking algorithm completes product scoring
 */
export const trackProductRankingComplete = (
  specificationId: string,
  productsRanked: number,
  rankingTimeMs: number,
  topScore: number,
  success: boolean = true
): void => {
  // Complete Apdex timing for product discovery journey
  const journeyMetrics = completeJourneyTiming(`discovery_${specificationId}`, success);

  // Track AI operation timing for P95/P99 SLOs
  trackAIOperationTiming(specificationId, 'ranking', rankingTimeMs, success);

  datadogRum.addAction('product_ranking_complete', {
    specification: {
      id: specificationId,
    },
    ranking: {
      products_ranked: productsRanked,
      ranking_time_ms: rankingTimeMs,
      top_score: topScore,
      products_per_second: productsRanked / (rankingTimeMs / 1000),
    },
    journey: {
      name: 'product_discovery',
      flow_stage: 'completion',
      duration_ms: journeyMetrics?.duration_ms,
      apdex_category: journeyMetrics?.apdex_category,
      apdex_threshold_satisfied: journeyMetrics?.apdex_threshold_satisfied,
      apdex_threshold_tolerating: journeyMetrics?.apdex_threshold_tolerating,
    },
    sli: {
      success: success,
      ranking_within_p95: rankingTimeMs <= 60000,
      ranking_within_p99: rankingTimeMs <= 120000,
      slo_threshold_p95: 60000,
      slo_threshold_p99: 120000,
    },
  });
};

/**
 * RUM-016: Track product card viewed
 * Trigger: User views product card details
 */
export const trackProductCardViewed = (
  productId: string,
  specificationId: string,
  viewDurationMs: number,
  productRank: number
): void => {
  datadogRum.addAction('product_card_viewed', {
    product: {
      id: productId,
      rank: productRank,
    },
    specification: {
      id: specificationId,
    },
    engagement: {
      view_duration_ms: viewDurationMs,
      timestamp: Date.now(),
    },
  });
};

/**
 * RUM-017: Track product shortlisted
 * Trigger: User adds product to shortlist
 */
export const trackProductShortlisted = (
  productId: string,
  specificationId: string,
  productRank: number,
  shortlistSize: number
): void => {
  datadogRum.addAction('product_shortlisted', {
    product: {
      id: productId,
      rank: productRank,
    },
    specification: {
      id: specificationId,
    },
    shortlist: {
      current_size: shortlistSize,
      target_size: 6,
    },
  });
};

/**
 * RUM-018: Track product removed from shortlist
 * Trigger: User removes product from shortlist
 */
export const trackProductRemovedFromShortlist = (
  productId: string,
  specificationId: string,
  timeOnShortlistMs: number,
  removalReason?: string
): void => {
  datadogRum.addAction('product_removed_from_shortlist', {
    product: {
      id: productId,
    },
    specification: {
      id: specificationId,
    },
    removal: {
      time_on_shortlist_ms: timeOnShortlistMs,
      reason: removalReason,
    },
  });
};

/**
 * RUM-019: Track product filter applied
 * Trigger: User applies filter to product results
 */
export const trackProductFilterApplied = (
  specificationId: string,
  filterType: string,
  filterValue: string,
  resultCount: number
): void => {
  datadogRum.addAction('product_filter_applied', {
    specification: {
      id: specificationId,
    },
    filter: {
      type: filterType,
      value: filterValue,
      result_count: resultCount,
    },
  });
};

/**
 * RUM-020: Track product sort changed
 * Trigger: User changes product sort order
 */
export const trackProductSortChanged = (
  specificationId: string,
  sortField: string,
  sortDirection: 'asc' | 'desc'
): void => {
  datadogRum.addAction('product_sort_changed', {
    specification: {
      id: specificationId,
    },
    sort: {
      field: sortField,
      direction: sortDirection,
    },
  });
};

/**
 * RUM-021: Track competitive index viewed
 * Trigger: User views competitive index analysis
 */
export const trackCompetitiveIndexViewed = (
  specificationId: string,
  viewDurationMs: number
): void => {
  datadogRum.addAction('competitive_index_viewed', {
    specification: {
      id: specificationId,
    },
    engagement: {
      view_duration_ms: viewDurationMs,
    },
  });
};

/**
 * RUM-022: Track product details expanded
 * Trigger: User expands product details section
 */
export const trackProductDetailsExpanded = (
  productId: string,
  specificationId: string,
  detailSection: string
): void => {
  datadogRum.addAction('product_details_expanded', {
    product: {
      id: productId,
    },
    specification: {
      id: specificationId,
    },
    details: {
      section: detailSection,
    },
  });
};

// ============================================================================
// Journey 3: Product Comparison (8 actions)
// ============================================================================

/**
 * RUM-023: Track product comparison start
 * Trigger: User initiates product comparison view
 */
export const trackProductComparisonStart = (
  specificationId: string,
  productCount: number,
  requirementCount: number
): void => {
  // Start Apdex timing for product comparison journey
  startJourneyTiming(`comparison_${specificationId}`, 'product_comparison');

  datadogRum.addAction('product_comparison_start', {
    specification: {
      id: specificationId,
      requirement_count: requirementCount,
    },
    comparison: {
      product_count: productCount,
      comparison_complexity: productCount * requirementCount,
    },
    journey: {
      name: 'product_comparison',
      flow_stage: 'initiation',
      apdex_threshold_satisfied: APDEX_THRESHOLDS.product_comparison.satisfied,
      apdex_threshold_tolerating: APDEX_THRESHOLDS.product_comparison.tolerating,
    },
    sli: {
      timing_started: true,
      timestamp: Date.now(),
    },
  });
};

/**
 * RUM-024: Track requirement verification initiated
 * Trigger: AI verification starts for product-requirement pair
 */
export const trackProductRequirementVerificationInitiated = (
  productId: string,
  requirementId: string,
  verificationType: 'automatic' | 'manual_trigger'
): void => {
  datadogRum.addAction('product_requirement_verification_initiated', {
    product: {
      id: productId,
    },
    requirement: {
      id: requirementId,
      verification_type: verificationType,
    },
    performance: {
      start_time: Date.now(),
    },
  });
};

/**
 * RUM-025: Track requirement verification complete
 * Trigger: AI verification completes for product-requirement
 */
export const trackProductRequirementVerificationComplete = (
  productId: string,
  requirementId: string,
  verificationResult: 'yes' | 'no' | 'partial' | 'unknown',
  verificationTimeMs: number,
  confidenceScore: number
): void => {
  // Track AI operation timing for verification
  trackAIOperationTiming(`${productId}_${requirementId}`, 'verification', verificationTimeMs, verificationResult !== 'unknown');

  datadogRum.addAction('product_requirement_verification_complete', {
    product: {
      id: productId,
    },
    requirement: {
      id: requirementId,
      result: verificationResult,
      confidence_score: confidenceScore,
    },
    performance: {
      verification_time_ms: verificationTimeMs,
    },
  });
};

/**
 * RUM-026: Track compliance check passed
 * Trigger: Product passes compliance check (GDPR, NSS, MITRE, NIST)
 */
export const trackProductComplianceCheckPassed = (
  productId: string,
  complianceType: string,
  checkDurationMs: number
): void => {
  datadogRum.addAction('product_compliance_check_passed', {
    product: {
      id: productId,
    },
    compliance: {
      type: complianceType,
      result: 'passed',
      check_duration_ms: checkDurationMs,
    },
  });
};

/**
 * RUM-027: Track compliance check failed
 * Trigger: Product fails compliance check
 */
export const trackProductComplianceCheckFailed = (
  productId: string,
  complianceType: string,
  failureReason: string
): void => {
  datadogRum.addAction('product_compliance_check_failed', {
    product: {
      id: productId,
    },
    compliance: {
      type: complianceType,
      result: 'failed',
      failure_reason: failureReason,
    },
  });
};

/**
 * RUM-028: Track comparison report generated
 * Trigger: User generates comparison report
 */
export const trackComparisonReportGenerated = (
  specificationId: string,
  productCount: number,
  reportFormat: 'pdf' | 'docx' | 'xlsx',
  generationTimeMs: number,
  success: boolean = true
): void => {
  // Complete Apdex timing for product comparison journey
  const journeyMetrics = completeJourneyTiming(`comparison_${specificationId}`, success);

  datadogRum.addAction('comparison_report_generated', {
    specification: {
      id: specificationId,
    },
    report: {
      product_count: productCount,
      format: reportFormat,
      generation_time_ms: generationTimeMs,
    },
    journey: {
      name: 'product_comparison',
      flow_stage: 'completion',
      duration_ms: journeyMetrics?.duration_ms,
      apdex_category: journeyMetrics?.apdex_category,
      apdex_threshold_satisfied: journeyMetrics?.apdex_threshold_satisfied,
      apdex_threshold_tolerating: journeyMetrics?.apdex_threshold_tolerating,
    },
    sli: {
      success: success,
      generation_within_p95: generationTimeMs <= 30000,
      generation_within_p99: generationTimeMs <= 60000,
      slo_threshold_p95: 30000,
      slo_threshold_p99: 60000,
    },
  });
};

/**
 * RUM-029: Track product scored
 * Trigger: Product receives final compliance/match score
 */
export const trackProductScored = (
  productId: string,
  specificationId: string,
  score: number,
  maxScore: number,
  scoringFactors: Record<string, number>
): void => {
  datadogRum.addAction('product_scored', {
    product: {
      id: productId,
      score: score,
      max_score: maxScore,
      score_percentage: (score / maxScore) * 100,
    },
    specification: {
      id: specificationId,
    },
    scoring: {
      factors: scoringFactors,
    },
  });
};

/**
 * RUM-030: Track product pinned
 * Trigger: User pins product for easy reference
 */
export const trackProductPinned = (
  productId: string,
  specificationId: string,
  productRank: number
): void => {
  datadogRum.addAction('product_pinned', {
    product: {
      id: productId,
      rank: productRank,
    },
    specification: {
      id: specificationId,
    },
  });
};

// ============================================================================
// Journey 4: Collaboration (6 actions)
// ============================================================================

/**
 * RUM-031: Track specification shared
 * Trigger: User shares specification with others
 */
export const trackSpecificationShared = (
  specificationId: string,
  shareMethod: 'email' | 'link' | 'internal',
  recipientCount: number
): void => {
  datadogRum.addAction('specification_shared', {
    specification: {
      id: specificationId,
    },
    sharing: {
      method: shareMethod,
      recipient_count: recipientCount,
    },
  });
};

/**
 * RUM-032: Track team member invited
 * Trigger: User invites team member to specification
 */
export const trackTeamMemberInvited = (
  specificationId: string,
  inviteeRole: string
): void => {
  datadogRum.addAction('team_member_invited', {
    specification: {
      id: specificationId,
    },
    invitation: {
      invitee_role: inviteeRole,
    },
  });
};

/**
 * RUM-033: Track supplier invited
 * Trigger: User invites supplier for product responses
 */
export const trackSupplierInvited = (
  specificationId: string,
  productId: string
): void => {
  datadogRum.addAction('supplier_invited', {
    specification: {
      id: specificationId,
    },
    product: {
      id: productId,
    },
  });
};

/**
 * RUM-034: Track notification received
 * Trigger: User receives in-app notification
 */
export const trackNotificationReceived = (
  notificationId: string,
  notificationType: string,
  relatedEntityId?: string
): void => {
  datadogRum.addAction('notification_received', {
    notification: {
      id: notificationId,
      type: notificationType,
      related_entity_id: relatedEntityId,
    },
  });
};

/**
 * RUM-035: Track notification clicked
 * Trigger: User clicks notification
 */
export const trackNotificationClicked = (
  notificationId: string,
  notificationType: string,
  timeToClickMs: number
): void => {
  datadogRum.addAction('notification_clicked', {
    notification: {
      id: notificationId,
      type: notificationType,
      time_to_click_ms: timeToClickMs,
    },
  });
};

/**
 * RUM-036: Track collaborative edit made
 * Trigger: User makes edit in collaborative session
 */
export const trackCollaborativeEditMade = (
  specificationId: string,
  editType: string,
  activeUsers: number
): void => {
  datadogRum.addAction('collaborative_edit_made', {
    specification: {
      id: specificationId,
    },
    collaboration: {
      edit_type: editType,
      active_users: activeUsers,
    },
  });
};

// ============================================================================
// Journey 5: Authentication (5 actions)
// ============================================================================

/**
 * RUM-037: Track login success
 * Trigger: User successfully logs in
 */
export const trackUserLoginSuccess = (
  userId: string,
  loginMethod: 'email_password' | 'sso' | 'magic_link' | 'google',
  loginDurationMs: number
): void => {
  // Calculate Apdex category for authentication
  const authThresholds = APDEX_THRESHOLDS.authentication;
  let apdexCategory: 'satisfied' | 'tolerating' | 'frustrated';

  if (loginDurationMs <= authThresholds.satisfied) {
    apdexCategory = 'satisfied';
  } else if (loginDurationMs <= authThresholds.tolerating) {
    apdexCategory = 'tolerating';
  } else {
    apdexCategory = 'frustrated';
  }

  datadogRum.addAction('user_login_success', {
    user: {
      id: userId,
    },
    authentication: {
      method: loginMethod,
      duration_ms: loginDurationMs,
      success: true,
    },
    journey: {
      name: 'authentication',
      flow_stage: 'completion',
      duration_ms: loginDurationMs,
      apdex_category: apdexCategory,
      apdex_threshold_satisfied: authThresholds.satisfied,
      apdex_threshold_tolerating: authThresholds.tolerating,
    },
    sli: {
      success: true,
      login_within_p95: loginDurationMs <= 1000,
      login_within_p99: loginDurationMs <= 2000,
      slo_threshold_p95: 1000,
      slo_threshold_p99: 2000,
    },
  });
};

/**
 * RUM-038: Track login failure
 * Trigger: User login attempt fails
 */
export const trackUserLoginFailure = (
  attemptedEmail: string,
  failureReason: string,
  attemptNumber: number
): void => {
  datadogRum.addAction('user_login_failure', {
    authentication: {
      attempted_email: attemptedEmail,
      failure_reason: failureReason,
      attempt_number: attemptNumber,
      success: false,
    },
  });
};

/**
 * RUM-039: Track logout
 * Trigger: User logs out
 */
export const trackUserLogout = (
  userId: string,
  sessionDurationMs: number,
  logoutReason: 'manual' | 'session_expired' | 'security'
): void => {
  datadogRum.addAction('user_logout', {
    user: {
      id: userId,
    },
    session: {
      duration_ms: sessionDurationMs,
      logout_reason: logoutReason,
    },
  });
};

/**
 * RUM-040: Track session expired
 * Trigger: User session expires
 */
export const trackSessionExpired = (
  userId: string,
  sessionDurationMs: number,
  activeTabsCount: number
): void => {
  datadogRum.addAction('session_expired', {
    user: {
      id: userId,
    },
    session: {
      duration_ms: sessionDurationMs,
      active_tabs: activeTabsCount,
      expiration_reason: 'timeout',
    },
  });
};

/**
 * RUM-041: Track password reset requested
 * Trigger: User requests password reset
 */
export const trackPasswordResetRequested = (
  email: string,
  requestSource: 'login_page' | 'settings' | 'support'
): void => {
  datadogRum.addAction('password_reset_requested', {
    authentication: {
      email: email,
      request_source: requestSource,
    },
  });
};

// ============================================================================
// Error Tracking (2 actions)
// ============================================================================

/**
 * RUM-042: Track error encountered
 * Trigger: JavaScript error occurs in application
 */
export const trackErrorEncountered = (
  error: Error,
  errorContext: Record<string, unknown>,
  lastUserAction?: string
): void => {
  datadogRum.addAction('error_encountered', {
    error: {
      type: error.name,
      message: error.message,
      stack: error.stack,
      context: errorContext,
    },
    page: {
      url: window.location.href,
      referrer: document.referrer,
    },
    user_action: {
      last_action: lastUserAction,
    },
  });
};

/**
 * RUM-043: Track API error
 * Trigger: API request fails
 */
export const trackAPIError = (
  endpoint: string,
  method: string,
  statusCode: number,
  errorMessage: string,
  requestDurationMs: number,
  traceId?: string,
  spanId?: string
): void => {
  datadogRum.addAction('api_error', {
    api: {
      endpoint: endpoint,
      method: method,
      status_code: statusCode,
      error_message: errorMessage,
      duration_ms: requestDurationMs,
    },
    correlation: {
      trace_id: traceId,
      span_id: spanId,
    },
  });
};

// ============================================================================
// Global Error Handlers Setup
// ============================================================================

let lastUserAction: string | undefined;

/**
 * Set the last user action for error context
 * Call this before important user actions
 */
export const setLastUserAction = (action: string): void => {
  lastUserAction = action;
};

/**
 * Setup global error handlers for automatic error tracking
 * Call this once during app initialization
 */
export const setupGlobalErrorHandlers = (): void => {
  // Capture unhandled errors
  window.addEventListener('error', (event) => {
    trackErrorEncountered(
      event.error || new Error(event.message),
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        type: 'unhandled_error',
      },
      lastUserAction
    );
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));

    trackErrorEncountered(
      error,
      {
        type: 'unhandled_promise_rejection',
      },
      lastUserAction
    );
  });
};
